import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
function getToken() { return localStorage.getItem('hrtrack_admin_token'); }

// Small labelled text input.
function Field({ label, hint, value, onChange, placeholder, type = 'text' }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{label}</label>
      {hint && <div style={{ color: 'var(--muted)', fontSize: '0.78rem', marginBottom: 6 }}>{hint}</div>}
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit' }}
      />
    </div>
  );
}

// Secret field: shows current status (set via env / db, masked) and lets you
// enter a new value. Leaving it as the masked placeholder keeps the existing one.
function SecretField({ label, hint, state, value, onChange }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>{label}</label>
      {hint && <div style={{ color: 'var(--muted)', fontSize: '0.78rem', marginBottom: 6 }}>{hint}</div>}
      <div style={{ fontSize: '0.78rem', marginBottom: 6 }}>
        {state?.set
          ? <span style={{ color: '#16a34a', fontWeight: 600 }}>● Configured ({state.source === 'env' ? 'from .env' : 'saved'}) — {state.masked}</span>
          : <span style={{ color: '#d97706', fontWeight: 600 }}>● Not set</span>}
      </div>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={state?.set ? 'Enter a new value to replace, or leave blank' : 'Paste key…'}
        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'monospace' }}
      />
    </div>
  );
}

const Card = ({ title, children }) => (
  <div style={{ background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: 24, marginBottom: 20 }}>
    <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 16 }}>{title}</div>
    {children}
  </div>
);

export default function OwnerIntegrations({ onAuthExpired }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Editable field buffers. Secret fields start empty (never prefilled).
  const [form, setForm] = useState({});

  const headers = { 'x-admin-token': getToken() };

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await axios.get(`${API}/api/owner/integrations`, { headers });
      setData(res.data);
      setForm({
        stripe_secret_key: '',
        stripe_webhook_secret: '',
        stripe_publishable_key: res.data.config.stripe_publishable_key || '',
        stripe_price_starter: res.data.config.stripe_price_starter || '',
        stripe_price_growth: res.data.config.stripe_price_growth || '',
        stripe_price_business: res.data.config.stripe_price_business || '',
        crisp_website_id: res.data.config.crisp_website_id || '',
        status_url: res.data.config.status_url || '',
      });
    } catch (e) {
      if (e.response?.status === 401) { onAuthExpired?.(); }
      else setErr('Failed to load integrations');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg(''); setErr('');
    // Only send secret fields when the operator typed something new.
    const body = { ...form };
    if (!body.stripe_secret_key) delete body.stripe_secret_key;
    if (!body.stripe_webhook_secret) delete body.stripe_webhook_secret;
    try {
      await axios.put(`${API}/api/owner/integrations`, body, { headers });
      setMsg('Saved. Changes take effect immediately — no redeploy needed.');
      await load();
    } catch (e) {
      if (e.response?.status === 401) { onAuthExpired?.(); }
      else setErr(e.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      {!data?.encryption?.dedicated_key && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: '0.82rem' }}>
          ⚠️ Secrets are encrypted using a key derived from <code>JWT_SECRET</code> (no dedicated <code>CONFIG_ENC_KEY</code> set). Set <code>CONFIG_ENC_KEY</code> in the VPS <code>.env</code> for a dedicated encryption key. Changing whichever key is in use makes saved secrets unreadable.
        </div>
      )}

      {msg && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: '0.85rem' }}>{msg}</div>}
      {err && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 10, padding: '10px 14px', marginBottom: 20, fontSize: '0.85rem' }}>{err}</div>}

      <Card title="Stripe — Billing">
        <SecretField label="Secret key" hint="sk_live_… — encrypted at rest. Never shown in full after saving." state={data?.secrets?.stripe_secret_key} value={form.stripe_secret_key} onChange={set('stripe_secret_key')} />
        <SecretField label="Webhook signing secret" hint="whsec_… — from your Stripe webhook endpoint (event: customer.subscription.*)." state={data?.secrets?.stripe_webhook_secret} value={form.stripe_webhook_secret} onChange={set('stripe_webhook_secret')} />
        <Field label="Publishable key" hint="pk_live_… — public, safe to expose." value={form.stripe_publishable_key} onChange={set('stripe_publishable_key')} placeholder="pk_live_…" />
        <div style={{ borderTop: '1px solid var(--hairline)', margin: '4px 0 16px' }} />
        <Field label="Price ID — Starter ($49)" value={form.stripe_price_starter} onChange={set('stripe_price_starter')} placeholder="price_…" />
        <Field label="Price ID — Growth ($99)" value={form.stripe_price_growth} onChange={set('stripe_price_growth')} placeholder="price_…" />
        <Field label="Price ID — Business ($199)" value={form.stripe_price_business} onChange={set('stripe_price_business')} placeholder="price_…" />
        <div style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>Webhook endpoint URL: <code>{API || 'https://app.hrtrackings.com'}/api/webhooks/stripe</code></div>
      </Card>

      <Card title="Crisp — Support chat">
        <Field label="Website ID" hint="From Crisp → Settings → Website Settings → Setup. The chat widget turns on for everyone the moment you save." value={form.crisp_website_id} onChange={set('crisp_website_id')} placeholder="xxxxxxxx-xxxx-…" />
      </Card>

      <Card title="Status page">
        <Field label="Public status URL" hint="Instatus (or similar) page. Shows a “System status” link on the login screen." value={form.status_url} onChange={set('status_url')} placeholder="https://status.hrtrackings.com" />
      </Card>

      <button onClick={save} disabled={saving} style={{ background: 'var(--primary, #4f46e5)', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}>
        {saving ? 'Saving…' : 'Save integrations'}
      </button>
    </div>
  );
}
